import { DeleteSystemAlertReq, MetadataForQuery } from '../../../shared/dto/ReqDtoRepository';
import { AdminSystemAlertListResponse, AdminSystemAlertResponse } from '../../../shared/dto/ResDtoRepository';
import { useApiWrapper } from '../../../shared/hooks/useApi/apiWrapper';
import { usePageStore } from '../../../shared/store/pageStore';
import { useAdminSystemAlertListStore, useAdminSystemAlertViewStore } from '../store/adminSystemAlertStore';
import { SystemAlertData } from '../../../shared/dto/EntityRepository';
import { CreateSystemAlertReq } from '../../../shared/dto/ReqDtoRepository';
import { createSystemAlert, endSystemAlert, deleteSystemAlert, getSystemAlertList } from '../api/adminApi';
import { useTempAlertStore } from '../../../shared/store/portal/tempAlertStore';
import { SimpleStringResponse } from '../../../shared/dto/ResDtoRepository';


interface UseMemberResult {
  activeSystemAlert: SystemAlertData | null;
  setActiveSystemAlert: (systemAlert: SystemAlertData | null) => void;
  systemAlertList: SystemAlertData[];
  getSystemAlertList: (metaData: MetadataForQuery) => Promise<void>;
  createSystemAlert: (data: CreateSystemAlertReq) => Promise<void>;
  endSystemAlert: (data: string) => Promise<void>;
  deleteSystemAlert: (data: DeleteSystemAlertReq) => Promise<void>;
}

export const useAdminSystemAlert = (): UseMemberResult => {
  const { activeSystemAlert, setActiveSystemAlert } = useAdminSystemAlertViewStore();
  const { systemAlertList, setSystemAlertList } = useAdminSystemAlertListStore();
  const { setUpdatedTempAlert, setDeletedTempAlert } = useTempAlertStore();
  const { setPage } = usePageStore();
  const withApiHandler = useApiWrapper();

  const handleGetSystemAlertListSuccess = (response: AdminSystemAlertListResponse) => {
    const data = response.data;
    setPage(data);
    setSystemAlertList(data.content);
  };

  const handleCreateSystemAlertSuccess = (response: AdminSystemAlertResponse) => {
    const data = response.data;
    setSystemAlertList([...systemAlertList, data]);
    setActiveSystemAlert(data);
  };

  const handleEndSystemAlertSuccess = (response: AdminSystemAlertResponse) => {
    const data = response.data;
    setActiveSystemAlert(data);
    setUpdatedTempAlert();
  };

  const handleDeleteSystemAlertSuccess = (response: SimpleStringResponse) => {
    const data = response.data;
    setActiveSystemAlert(null);
    setSystemAlertList(systemAlertList.filter(each => each.id !== data.value));
    setDeletedTempAlert();
  };

  const handleSystemAlertRequest = async (
    action: 'get' | 'create' | 'end' | 'delete',
    data?: CreateSystemAlertReq | DeleteSystemAlertReq | string,
    metaData?: MetadataForQuery
  ) => {  
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          handleCreateSystemAlertSuccess(await createSystemAlert(data as CreateSystemAlertReq));
          break;
        case 'end':
          handleEndSystemAlertSuccess(await endSystemAlert(data as string));
          break;
        case 'delete':
          handleDeleteSystemAlertSuccess(await deleteSystemAlert(data as DeleteSystemAlertReq));
          break;
        case 'get':
          default:
          handleGetSystemAlertListSuccess(await getSystemAlertList(metaData!));
          break;
      }
    };

    await withApiHandler(apiFunction, [action, metaData]);
  };

  const getSystemAlertListHandler = (metaData: MetadataForQuery) => handleSystemAlertRequest('get', undefined, metaData);
  const createSystemAlertHandler = (data: CreateSystemAlertReq) => handleSystemAlertRequest('create', data);
  const endSystemAlertHandler = (systemAlertId: string) => handleSystemAlertRequest('end', systemAlertId);
  const deleteSystemAlertHandler = (data: DeleteSystemAlertReq) => handleSystemAlertRequest('delete', data);

  return {
    activeSystemAlert,
    setActiveSystemAlert,
    systemAlertList,
    getSystemAlertList: getSystemAlertListHandler,
    createSystemAlert: createSystemAlertHandler,
    endSystemAlert: endSystemAlertHandler,
    deleteSystemAlert: deleteSystemAlertHandler,
  };
};